import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';

const FundTransferModal = ({ show, handleClose, transferType, contactInfo, onSubmit }) => {
  const [formData, setFormData] = useState({
    beneficiaryName: '',
    accountNumber: '',
    ifscCode: '',
    amount: '',
    remark: ''
  });

  const [errors, setErrors] = useState({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setFormData({
        beneficiaryName: '',
        accountNumber: '',
        ifscCode: '',
        amount: '',
        remark: ''
      });
      setErrors({});
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.beneficiaryName.trim()) {
      newErrors.beneficiaryName = 'Beneficiary name is required';
    }

    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!/^\d{9,18}$/.test(formData.accountNumber)) {
      newErrors.accountNumber = 'Account number must be 9-18 digits';
    }

    if (!formData.ifscCode.trim()) {
      newErrors.ifscCode = 'IFS Code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.toUpperCase())) {
      newErrors.ifscCode = 'Invalid IFS Code format';
    }

    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    } else if (parseFloat(formData.amount) > 200000) {
      newErrors.amount = 'Amount cannot exceed ₹2,00,000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const transferData = {
        ...formData,
        contactInfo,
        transferType,
        transactionDate: new Date().toISOString().split('T')[0]
      };
      onSubmit(transferData);
      handleClose();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="custom-modal-width">
      <Modal.Header closeButton style={{ borderBottom: '1px solid #eef2ff', padding: '1rem 1.4rem' }}>
        <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1f2937' }}>
          <i className="bi bi-cash-stack me-2" style={{ color: '#4f46e5' }}></i>
          New Fund Transfer
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1.4rem', background: '#f9fafb' }}>
          <div
            className="mb-3 p-3"
            style={{
              background: '#ffffff',
              borderRadius: '10px',
              border: '1px solid #e2e8f0'
            }}
          >
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
              Transfer via
            </div>
            <div className="d-flex justify-content-between align-items-center mt-2">
              <span style={{ fontWeight: 600, color: '#1e293b' }}>
                {transferType === 'mobile' ? 'Mobile Number' : 'Account Number'}
              </span>
              <span style={{ fontSize: '1rem', fontWeight: 600, color: '#4f46e5' }}>{contactInfo}</span>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>
              Beneficiary Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="beneficiaryName"
              placeholder="Enter beneficiary name"
              value={formData.beneficiaryName}
              onChange={handleChange}
              isInvalid={!!errors.beneficiaryName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.beneficiaryName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Account Number <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="accountNumber"
              placeholder="Enter beneficiary account number"
              value={formData.accountNumber}
              onChange={handleChange}
              isInvalid={!!errors.accountNumber}
              maxLength={18}
            />
            <Form.Control.Feedback type="invalid">
              {errors.accountNumber}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              IFS Code <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="ifscCode"
              placeholder="Enter IFS Code (e.g., SBIN0001234)"
              value={formData.ifscCode}
              onChange={handleChange}
              isInvalid={!!errors.ifscCode}
              maxLength={11}
              style={{ textTransform: 'uppercase' }}
            />
            <Form.Control.Feedback type="invalid">
              {errors.ifscCode}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Format: 4 letters + 0 + 6 characters
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Amount (₹) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              name="amount"
              placeholder="Enter amount"
              value={formData.amount}
              onChange={handleChange}
              isInvalid={!!errors.amount}
              min="1"
              max="200000"
              step="0.01"
            />
            <Form.Control.Feedback type="invalid">
              {errors.amount}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Minimum: ₹1 | Maximum: ₹2,00,000
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Remark <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="remark"
              placeholder="Enter remark or description"
              value={formData.remark}
              onChange={handleChange}
              maxLength={200}
            />
            <Form.Text className="text-muted">
              {formData.remark.length}/200 characters
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer style={{ padding: '1rem 1.4rem', borderTop: '1px solid #eef2ff', background: '#f9fafb' }}>
          <Button variant="light" onClick={handleClose} size="sm">
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            size="sm"
            style={{
              background: '#4f46e5',
              border: 'none',
              fontWeight: 600,
              padding: '0.45rem 1.3rem'
            }}
          >
            <i className="bi bi-send me-2"></i>
            Transfer Now
          </Button>
        </Modal.Footer>
      </Form>
      
      <style>{`
        .custom-modal-width .modal-dialog {
          max-width: 450px;
        }
        
        .custom-modal-width .modal-body::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-modal-width .modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .custom-modal-width .modal-body::-webkit-scrollbar-thumb {
          background: #667eea;
          border-radius: 10px;
        }
        
        .custom-modal-width .modal-body::-webkit-scrollbar-thumb:hover {
          background: #5568d3;
        }
      `}</style>
    </Modal>
  );
};

export default FundTransferModal;

